<!--
  FilterConditionsEditor.vue — Headless condition list for filter UIs.

  Parent provides a `useFilter` instance and the fields to pick from; the
  editor calls into the composable directly for mutations. Rows read as a
  sentence on a shared column grid — "Where <field> <operator> <value>", then
  "and"/"or" — and the second row's connector sets the logic for the group.
  Reverse-link fields (optional; hosts without a dynamic list behind them omit
  them) join the field picker as "Linked as" groups, one per source type.
-->
<template>
  <div class="filter-conditions-editor">
    <div
      v-for="(condition, index) in conditions"
      :key="condition.id"
      data-testid="filter-condition-row"
      class="condition-item"
      :class="{ 'is-condition-disabled': !condition.enabled }"
    >
      <div class="condition-connector text-7">
        <span v-if="index === 0" class="text-muted">Where</span>
        <ToolbarDropdown v-else-if="index === 1" width="7rem" data-testid="logic-dropdown">
          <template #button>
            <button class="btn btn-sm is-transparent" aria-haspopup="true">
              <span>{{ logic }}</span>
              <span class="icon is-small text-muted">
                <font-awesome-icon icon="chevron-down" style="height: 9px" />
              </span>
            </button>
          </template>
          <template #default="{ close }">
            <div
              v-for="mode in logicModes"
              :key="mode"
              class="dropdown-item text-7"
              @click="(setLogic(mode), close())"
            >
              <span class="grow">{{ mode === "and" ? "and — match all" : "or — match any" }}</span>
              <font-awesome-icon v-if="logic === mode" icon="check" class="text-primary" />
            </div>
          </template>
        </ToolbarDropdown>
        <span v-else class="text-muted">{{ logic }}</span>
      </div>

      <div class="condition-field">
        <FilterFieldsDropdown
          :fields="filterableFields"
          :field-groups="allFieldGroups"
          :selected="condition.field"
          @select="(field) => updateConditionField(condition.id, field.key)"
        />
      </div>

      <div class="condition-operator">
        <FilterOperatorsDropdown
          :selected="condition.operator"
          :field-type="getFieldType(condition.field)"
          :allowed="getField(condition.field)?.operators ?? null"
          @select="(op) => selectOperator(condition, op)"
        />
      </div>

      <div class="condition-value">
        <template v-if="getOperatorValueShape(condition.operator) !== 'none'">
          <div
            v-if="getOperatorValueShape(condition.operator) === 'range'"
            class="flex items-center g-2"
          >
            <input
              :type="getValueInputType(condition) === 'number' ? 'number' : 'date'"
              class="input is-small"
              :value="Array.isArray(condition.value) ? condition.value[0] : ''"
              @input="updateRangeBound(condition, 0, ($event.target as HTMLInputElement).value)"
            />
            <span class="text-muted text-7">and</span>
            <input
              :type="getValueInputType(condition) === 'number' ? 'number' : 'date'"
              class="input is-small"
              :value="Array.isArray(condition.value) ? condition.value[1] : ''"
              @input="updateRangeBound(condition, 1, ($event.target as HTMLInputElement).value)"
            />
          </div>
          <SpanInput
            v-else-if="getOperatorOperand(condition.operator) === 'span'"
            :model-value="typeof condition.value === 'string' ? condition.value : ''"
            @update:model-value="(val) => updateCondition(condition.id, { value: val })"
          />
          <input
            v-else-if="getOperatorOperand(condition.operator) === 'number'"
            type="number"
            min="0"
            class="input is-small"
            placeholder="Value..."
            :value="condition.value"
            @input="
              updateCondition(condition.id, {
                value:
                  ($event.target as HTMLInputElement).value === ''
                    ? ''
                    : Number(($event.target as HTMLInputElement).value),
              })
            "
          />
          <component
            :is="getFilterComponent(condition)"
            v-else-if="getFilterComponent(condition)"
            :model-value="condition.value"
            :field="filterField(condition)"
            :record-type="getFilterRecordType(condition)"
            :is-array="getOperatorValueShape(condition.operator) === 'array'"
            size="small"
            @update:model-value="(val: unknown) => updateCondition(condition.id, { value: val })"
          />
          <EnumMultiSelect
            v-else-if="
              getValueInputType(condition) === 'select' &&
              getOperatorValueShape(condition.operator) === 'array'
            "
            :model-value="Array.isArray(condition.value) ? condition.value : []"
            :field="getField(condition.field)"
            size="small"
            @update:model-value="(val: unknown) => updateCondition(condition.id, { value: val })"
          />
          <input
            v-else-if="getOperatorValueShape(condition.operator) === 'array'"
            type="text"
            class="input is-small"
            placeholder="Comma-separated values..."
            :value="Array.isArray(condition.value) ? condition.value.join(', ') : ''"
            @input="updateArrayValue(condition, ($event.target as HTMLInputElement).value)"
          />
          <input
            v-else-if="getValueInputType(condition) === 'number'"
            type="number"
            class="input is-small"
            placeholder="Value..."
            :value="condition.value"
            @input="
              updateCondition(condition.id, {
                value: parseFloat(($event.target as HTMLInputElement).value),
              })
            "
          />
          <input
            v-else-if="getValueInputType(condition) === 'date'"
            type="date"
            class="input is-small"
            :value="condition.value"
            @input="
              updateCondition(condition.id, { value: ($event.target as HTMLInputElement).value })
            "
          />
          <input
            v-else
            type="text"
            class="input is-small"
            placeholder="Value..."
            :value="condition.value"
            @input="
              updateCondition(condition.id, { value: ($event.target as HTMLInputElement).value })
            "
          />
        </template>
      </div>

      <div class="condition-actions">
        <ActionsDropdown
          :items="conditionMenu(condition)"
          @action="(key) => handleConditionAction(condition, key)"
        />
      </div>
    </div>

    <div class="condition-add">
      <button
        class="btn btn-sm is-transparent text-muted"
        :disabled="!filterableFields.length"
        @click="addCondition"
      >
        <span class="icon is-small"><font-awesome-icon icon="plus" /></span>
        <span>Add condition</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRaw } from "vue"
import { isJunctionField, isReferenceField } from "@auger/core/fields"
import { getOperatorByKey, getOperatorsForType } from "@auger/core/operators"
import ActionsDropdown from "@auger/ui/components/ActionsDropdown.vue"
import ToolbarDropdown from "@auger/ui/components/ToolbarDropdown.vue"
import { getComponent } from "@/common/fields/components"
import RecordsInput from "@/components/inputs/RecordsInput.vue"
import SchemaSelect from "@/components/selects/SchemaSelect.vue"
import TypeSelect from "@/components/selects/TypeSelect.vue"
import type { FilterCondition, Logic } from "@/composables/useFilter"
import { getType } from "@/stores/types"
import SpanInput from "./SpanInput.vue"

// Legacy `getOperatorValueShape(op)` → operator's value shape via the new
// registry; defaults to scalar for unknown operators.
function getOperatorOperand(operator: string | undefined | null): string | null {
  return (operator ? getOperatorByKey(operator)?.operand : undefined) ?? null
}

function getOperatorValueShape(operator: string | undefined | null): string {
  return (operator ? getOperatorByKey(operator)?.value : undefined) ?? "scalar"
}
import FilterFieldsDropdown from "@auger/ui/components/collection/FilterFieldsDropdown.vue"
import FilterOperatorsDropdown from "@auger/ui/components/collection/FilterOperatorsDropdown.vue"
import EnumMultiSelect from "@/components/selects/EnumMultiSelect.vue"
import type { AugerFilterFieldDef, UseFilterInstance } from "@/composables/useFilter"
import type { FilterFieldGroup } from "@/composables/useFilterFields"

interface Props {
  filters: UseFilterInstance
  fields: AugerFilterFieldDef[]
  fieldGroups?: FilterFieldGroup[] | null
  reverseLinkFields?: AugerFilterFieldDef[]
  recordType?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  fieldGroups: null,
  reverseLinkFields: () => [],
  recordType: null,
})

// `.value` access because both callers (FilterEditorModal, DynamicFilterSection
// via shallowRef) pass the raw useFilter return; inner Refs stay Refs. If a
// future caller wraps the instance in `ref()` (which auto-reactives and
// unwraps), the template would iterate the Ref's internal property keys
// instead of the array — visible as a handful of blank rows.
const conditions = computed(() => props.filters.conditions.value as FilterCondition[])
const logic = computed(() => props.filters.logic.value)

const logicModes: Logic[] = ["and", "or"]

const filterableFields = computed(() =>
  props.fields.filter((f) => f.isFilterable !== false && !f.isHidden)
)

// One "Linked as" group per source type, so a reverse link is picked in one
// step and its trigger reads "Founders · Organization".
const linkedAsGroups = computed(() => {
  const byType = new Map<string, AugerFilterFieldDef[]>()
  for (const f of props.reverseLinkFields) {
    const type = (f.sourceType as string) || "record"
    if (!byType.has(type)) byType.set(type, [])
    const typeLabel = getType(type)?.label || type
    byType.get(type)!.push({ ...f, triggerLabel: `${f.label} · ${typeLabel}` })
  }
  return [...byType].map(([type, fields]) => ({
    key: `linked-as-${type}`,
    label: `Linked as · ${getType(type)?.plural_label || type}`,
    fields,
  }))
})

const allFieldGroups = computed(() => {
  if (!props.fieldGroups && !linkedAsGroups.value.length) return null
  const base = props.fieldGroups
    ? props.fieldGroups.filter((g) => g.fields.length)
    : [{ key: "fields", label: "Fields", fields: filterableFields.value }]
  return [...base, ...linkedAsGroups.value]
})

// Tracks field keys we've already warned about so a stale condition
// referencing a deleted field doesn't flood the console on every re-render.
const warnedMissingFields = new Set<string>()

function getField(fieldKey: string) {
  // Main fields first, then fall back to reverse link fields
  const hit =
    props.fields.find((f) => f.key === fieldKey) ||
    props.reverseLinkFields.find((f) => f.key === fieldKey) ||
    null
  if (!hit && fieldKey && import.meta.env?.DEV && !warnedMissingFields.has(fieldKey)) {
    warnedMissingFields.add(fieldKey)
    // Dev-only breadcrumb: a stored condition referencing a field that no
    // longer exists (or was typo'd) will silently fall back to a text input
    // and accept garbage on save. This makes the mismatch visible without
    // leaking noise into production consoles.
    console.debug(
      `FilterConditionsEditor: no field found for key "${fieldKey}" — condition will use text-input fallback.`
    )
  }
  return hit
}

// The enriched field for a condition's value input (junction chips resolve via
// useFilter.enrich; others pass through).
function filterField(condition: FilterCondition): AugerFilterFieldDef | null {
  return props.filters.enrich(
    getField(condition.field),
    getOperatorValueShape(condition.operator) === "array",
    condition.value
  )
}

function getFieldType(fieldKey: string) {
  const field = getField(fieldKey)
  if (!field) return "string"
  // A reverse link offers its own operators, grouped as a string's.
  if (field.subtype === "reverse_links") return "string"
  return field.valueType ?? field.type
}

function getOperators(fieldKey: string) {
  const field = getField(fieldKey)
  if (!field) return ["contains", "equals"]
  if (field.operators) return field.operators
  return getOperatorsForType(getFieldType(fieldKey) ?? "")
}

/** The value-input component for a condition; null falls through to a native
 *  input. Operator/reverse-link overrides first, else the field's form renderer. */
function getFilterComponent(condition: FilterCondition) {
  const field = getField(condition.field)
  if (!field) return null

  if (condition.operator === "isType") return TypeSelect
  if (condition.operator === "listedIn") return RecordsInput
  if (field.subtype === "reverse_links") {
    if (condition.operator === "hasSchema") return SchemaSelect
    if (condition.operator === "includes" || condition.operator === "hasAny") return RecordsInput
    return null
  }

  // The registered filter component for select fields (ValueSelect) is a
  // native <select>. For array-shape operators (isOneOf/isNotOneOf), fall through so
  // the template's chip-widget fallback (EnumMultiSelect) handles it —
  // picking `is one of` on a Stage field then gets a chip picker rather than
  // a native multi-select listbox.
  if (field.type === "select" && getOperatorValueShape(condition.operator) === "array") {
    return null
  }

  return getComponent("form", { type: field.type ?? "string" })
}

function getFilterRecordType(condition: FilterCondition) {
  if (condition.operator === "listedIn") return "list"
  const field = getField(condition.field)
  if (field?.sourceType) return field.sourceType
  if ((field?.type === "link" || field?.type === "related") && field?.recordType)
    return field.recordType
  if (field?.type === "schema") return props.recordType
  return null
}

function updateRangeBound(condition: FilterCondition, index: 0 | 1, raw: string) {
  const isNumber = getValueInputType(condition) === "number"
  const parsed = isNumber ? (raw === "" ? null : parseFloat(raw)) : raw
  const current = Array.isArray(condition.value) ? [...condition.value] : [null, null]
  current[index] = parsed
  updateCondition(condition.id, { value: current })
}

function updateArrayValue(condition: FilterCondition, raw: string) {
  const items = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
  const isNumber = getValueInputType(condition) === "number"
  const parsed = isNumber ? items.map((s) => parseFloat(s)).filter((n) => !Number.isNaN(n)) : items
  updateCondition(condition.id, { value: parsed })
}

function getValueInputType(condition: FilterCondition) {
  const field = getField(condition.field)
  if (!field) return "text"
  if (field.values && field.values.length > 0) return "select"

  const typeMap: Record<string, string> = {
    string: "text",
    number: "number",
    date: "date",
    boolean: "text",
    select: "select",
  }
  return (field.type && typeMap[field.type]) || "text"
}

function getDefaultValueForField(fieldKey: string): FilterCondition["value"] {
  const field = getField(fieldKey)
  if (!field?.type) return ""
  // Reference / junction fields store ids, not raw text. Defaulting them to
  // "" poisons id-based operators (e.g. a `has any of` query with an empty
  // string) and renders a stray "Unknown ID" chip. Array-valued pickers
  // start as [], single pickers as null. Driven by the storage root so it
  // tracks the type system rather than a hardcoded subtype/type allowlist.
  const def = { type: field.type }
  if (isReferenceField(def) || isJunctionField(def)) {
    return field.isArray ? [] : null
  }
  return ""
}

function addCondition() {
  // The template gates the button on filterableFields.length via :disabled,
  // so hitting that branch in practice means the parent forgot to pass a
  // useFilter instance. Flag it loudly — a silent return here would look
  // like an inert button in the UI.
  if (!props.filters) {
    console.warn("FilterConditionsEditor: addCondition called without a `filters` prop.")
    return
  }
  const field = filterableFields.value[0]
  if (!field) return
  const operators = getOperators(field.key)
  props.filters.addCondition({
    field: field.key,
    operator: operators[0] || "contains",
    value: getDefaultValueForField(field.key),
  })
}

function updateCondition(id: string, updates: Partial<FilterCondition>) {
  props.filters.updateCondition(id, updates)
}

// "is listed in" holds list ids where the field's other operators hold its own
// records, so switching across that line clears the value.
function selectOperator(condition: FilterCondition, operator: string) {
  const crossesTarget = (condition.operator === "listedIn") !== (operator === "listedIn")
  const crossesOperand = getOperatorOperand(condition.operator) !== getOperatorOperand(operator)
  if (crossesTarget) updateCondition(condition.id, { operator, value: [] })
  else if (crossesOperand) updateCondition(condition.id, { operator, value: "" })
  else updateCondition(condition.id, { operator })
}

function setLogic(mode: "and" | "or") {
  props.filters.setLogic(mode)
}

function conditionMenu(condition: FilterCondition) {
  return [
    {
      key: "toggle",
      label: condition.enabled ? "Disable" : "Enable",
      icon: condition.enabled ? "eye-slash" : "eye",
    },
    { key: "duplicate", label: "Duplicate", icon: "clone" },
    { key: "remove", label: "Remove", icon: "trash", separator: true },
  ]
}

function handleConditionAction(condition: FilterCondition, key: string) {
  if (key === "toggle") props.filters.toggleCondition(condition.id)
  else if (key === "remove") props.filters.removeCondition(condition.id)
  else if (key === "duplicate") {
    const { field, operator, value, enabled } = condition
    props.filters.addCondition({ field, operator, value: structuredClone(toRaw(value)), enabled })
  }
}

function updateConditionField(id: string, newField: string) {
  const operators = getOperators(newField)
  props.filters.updateCondition(id, {
    field: newField,
    operator: operators[0] || "contains",
    value: getDefaultValueForField(newField),
  })
}
</script>

<style>
/* Rows share one column grid (subgrid) so every field, operator and value
   picker lines up regardless of its contents. */
.filter-conditions-editor {
  display: grid;
  grid-template-columns: 3.25rem minmax(0, 13rem) minmax(0, 10rem) minmax(8rem, 1fr) 2rem;
  column-gap: 0.5rem;
  row-gap: 0.5rem;
  align-items: center;

  .condition-item {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    align-items: center;

    &.is-condition-disabled {
      .condition-field,
      .condition-operator,
      .condition-value {
        opacity: 0.45;
      }

      .toolbar-dropdown .btn > span:nth-child(2) {
        text-decoration: line-through;
      }
    }

    .condition-actions {
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    &:hover .condition-actions,
    .condition-actions:focus-within,
    .condition-actions:has(.is-active) {
      opacity: 1;
    }
  }

  .condition-connector {
    padding-left: 0.25rem;

    .btn {
      padding-inline: 0;
      gap: 0.3em;
    }
  }

  /* Pickers fill their cell; long labels elide and the caret stays right. */
  .condition-field,
  .condition-operator {
    min-width: 0;

    .toolbar-dropdown,
    .toolbar-dropdown .dropdown-trigger,
    .toolbar-dropdown .btn {
      width: 100%;
      min-width: 0;
    }

    .toolbar-dropdown .btn {
      justify-content: flex-start;

      > span:nth-child(2) {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: left;
      }
    }
  }

  /* Value widgets come from the field registry with their own chrome (some
     borderless, some inline-flex on a taller line box); here they all get the
     pickers' bordered frame and height. */
  .condition-value {
    display: flex;
    align-items: center;
    min-width: 0;
    min-height: var(--auger-control-height-small);

    > * {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0;
    }

    /* The global 2px focus-ring margin on .select/.input would sit the control
       2px high in a row aligned on the grid. */
    .select,
    .input {
      margin-bottom: 0;
    }

    .select {
      display: flex;
      width: 100%;

      select {
        width: 100%;
      }
    }

    .select select,
    .input {
      border-color: var(--auger-input-border);
      background-color: var(--auger-bg);

      &:hover {
        border-color: var(--auger-border-hover);
      }
    }
  }

  .condition-actions .actions-trigger {
    width: 2rem;
    height: 2rem;
    font-size: var(--auger-size-6);
  }

  .condition-add {
    grid-column: 2 / -1;
  }
}

@media (hover: none) {
  .filter-conditions-editor .condition-item .condition-actions {
    opacity: 1;
  }
}
</style>
